<div class="migration-page">
  <div class="migration-header">
    <wm-common-text
      class="page-title"
      [data]="{ text: 'Workflow Migration', fontWeight: '500' }"
    ></wm-common-text>
    <wm-common-text
      class="page-subtitle"
      [data]="{
        text:
          'Manage workflow migrations and track workflow migration progress.'
      }"
    ></wm-common-text>
  </div>

  <div *ngIf="successMessage" class="page-banner success">
    <wm-common-text [data]="{ text: successMessage }"></wm-common-text>
  </div>

  <div *ngIf="errorMessage" class="page-banner error">
    <wm-common-text [data]="{ text: errorMessage }"></wm-common-text>
  </div>

  <section class="migration-card monitor-card" *ngIf="rows.length > 0">
    <div class="card-header-row monitor-header-row">
      <div>
        <wm-common-text
          class="card-title"
          [data]="{ text: 'Workflow Migration Status', fontWeight: '500' }"
        ></wm-common-text>
        <p class="card-subtitle">
          View workflow and application migration status across all visible
          migration processing records.
        </p>
      </div>
      <div class="monitor-actions">
        <button
          class="clear-button"
          type="button"
          (click)="loadMonitorStatus()"
        >
          Refresh Status
        </button>
        <button class="clear-button" type="button" (click)="toggleMonitor()">
          {{ monitorExpanded ? 'Collapse' : 'Expand' }}
        </button>
      </div>
    </div>

    <div *ngIf="monitorExpanded && monitorErrorMessage" class="monitor-error">
      {{ monitorErrorMessage }}
    </div>

    <div *ngIf="monitorExpanded" class="monitor-summary-grid">
      <div class="monitor-summary-card">
        <span class="monitor-label">Workflow Tasks</span>
        <strong>{{ runningWorkflowCount }} running</strong>
        <small
          >{{ queuedWorkflowCount }} queued,
          {{ completedWorkflowCount }} completed</small
        >
      </div>
      <div class="monitor-summary-card">
        <span class="monitor-label">Applications</span>
        <strong
          >{{ completedApplicationCount }} / {{ totalApplicationCount }}</strong
        >
        <small>{{ runningApplicationCount }} running</small>
      </div>
      <div class="monitor-summary-card">
        <span class="monitor-label">Needs Attention</span>
        <strong class="danger-text">{{ failedApplicationCount }}</strong>
        <small>failed application task(s)</small>
      </div>
    </div>

    <div *ngIf="monitorExpanded" class="monitor-filter-row">
      <label class="monitor-completed-toggle">
        <input
          type="checkbox"
          [checked]="showCompletedOlderThanWeek"
          (change)="toggleOlderCompleted()"
        />
        Show completed migrations older than 7 days
      </label>
    </div>

    <div *ngIf="monitorExpanded" class="monitor-table-wrapper">
      <div *ngIf="monitorRows.length === 0" class="monitor-empty-state">
        No active or recently completed migrations to display.
      </div>
      <table class="monitor-table" *ngIf="monitorRows.length > 0">
        <thead>
          <tr>
            <th>Migration</th>
            <th>Workflow</th>
            <th>Applications</th>
            <th>Progress</th>
            <th>Queued On</th>
            <th>Executed On</th>
            <th>Completed On</th>
            <th>Queued Duration</th>
            <th>Execution Duration</th>
            <th>Owner</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of monitorRows">
            <td>
              <strong>{{ row.name }}</strong>
              <div class="muted-text">{{ row.jurisdiction }}</div>
            </td>
            <td>
              <span
                class="status-pill"
                [ngClass]="getStatusClass(row.workflowStatus)"
              >
                {{ row.workflowStatus }}
              </span>
            </td>
            <td>
              <span
                class="status-pill"
                [ngClass]="getStatusClass(row.applicationStatus)"
              >
                {{ row.applicationStatus }}
              </span>
              <div class="muted-text">
                {{ row.runningApplications }} running,
                {{ row.queuedApplications }} queued,
                {{ row.failedApplications }} failed
              </div>
              <div *ngIf="row.latestError" class="row-error">
                {{ row.latestError }}
              </div>
            </td>
            <td>
              <div class="progress-label">
                <span
                  >{{ row.completedApplications }} /
                  {{ row.totalApplications }}</span
                >
                <span>{{ row.progressPercent }}%</span>
              </div>
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  [style.width.%]="row.progressPercent"
                ></div>
              </div>
            </td>
            <td>{{ row.queuedOn }}</td>
            <td>{{ row.executedOn }}</td>
            <td>{{ row.completedOn }}</td>
            <td>{{ row.queuedDuration }}</td>
            <td>{{ row.executionDuration }}</td>
            <td>{{ row.ownerName }}</td>
            <td>
              <button
                class="inline-link-button"
                type="button"
                (click)="openDetail(row.id)"
              >
                Open
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="monitor-pagination" *ngIf="monitorRecordCount > 0">
        <wm-common-pagination
          class="common-table-pagination"
          [data]="monitorPaginationData"
          (pageSizeChange)="onMonitorPageSizeChange($event)"
          (pageSelect)="onMonitorPageChange($event)"
          (first)="goToFirstMonitorPage()"
          (previous)="goToPreviousMonitorPage()"
          (next)="goToNextMonitorPage()"
          (last)="goToLastMonitorPage()"
        ></wm-common-pagination>
      </div>
    </div>
  </section>

  <section class="migration-card">
    <div class="card-header-row">
      <div>
        <wm-common-text
          class="card-title"
          [data]="{ text: 'Workflow Migrations', fontWeight: '500' }"
        ></wm-common-text>
      </div>
      <div class="header-actions">
        <wm-common-button
          class="create-button"
          [data]="{
            text: 'CREATE NEW',
            variant: 'basic',
            onClick: openCreate.bind(this)
          }"
        ></wm-common-button>
      </div>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <div class="search-row">
        <input
          type="text"
          class="search-input"
          placeholder="Search by jurisdiction, source system, workflow type, or owner..."
          [(ngModel)]="searchTerm"
          (keyup.enter)="search()"
        />
        <button class="search-button" (click)="search()">Search</button>
        <button class="clear-button" (click)="clearSearch()">Clear</button>
      </div>
    </div>

    <div *ngIf="!isLoading && rows.length === 0" class="empty-state">
      <wm-common-text
        [data]="{
          text: 'No workflow migrations ready to start for this jurisdiction.',
          fontWeight: '500'
        }"
      ></wm-common-text>
      <wm-common-text
        [data]="{
          text: 'Workflow migrations that are ready to start will appear here.'
        }"
      ></wm-common-text>
      <div class="empty-action">
        <wm-common-button
          [data]="{
            text: 'CREATE NEW WORKFLOW MIGRATION',
            variant: 'basic',
            onClick: openCreate.bind(this)
          }"
        ></wm-common-button>
      </div>
    </div>

    <div class="table-wrapper" *ngIf="isLoading || rows.length > 0">
      <wm-common-table
        tableType="Staff"
        [columns]="columns"
        [data]="rows"
        [isLoading]="isLoading"
        [errorMessage]="errorMessage"
        [showRetryButton]="false"
        [fullHeight]="rows.length > 7"
        tableHeight="560px"
      ></wm-common-table>
    </div>
  </section>
</div>
